import { HttpErrorResponse } from '@angular/common/http';

interface ValidationErrorBody {
  title?: string;
  message?: string;
  errors?: Record<string, string[] | string>;
}

function readErrorBody(error: HttpErrorResponse): ValidationErrorBody | null {
  if (typeof error.error === 'object' && error.error !== null) {
    return error.error as ValidationErrorBody;
  }
  if (typeof error.error === 'string' && error.error.length > 0) {
    return { message: error.error };
  }
  return null;
}

function firstFieldValidationMessage(
  errors: Record<string, string[] | string>,
): string | null {
  const firstKey = Object.keys(errors)[0];
  if (!firstKey) {
    return null;
  }
  const first = errors[firstKey];
  if (Array.isArray(first)) {
    return first[0] ?? null;
  }
  if (typeof first === 'string') {
    return first;
  }
  return null;
}

/**
 * Extracts a user-facing validation message from a 4xx body (title, field errors).
 * Returns null when no structured validation payload is present.
 */
export function resolveHttpValidationMessage(
  error: HttpErrorResponse,
): string | null {
  const body = readErrorBody(error);
  if (!body) {
    return null;
  }

  if (body.errors && typeof body.errors === 'object') {
    const fieldMessage = firstFieldValidationMessage(body.errors);
    if (fieldMessage) {
      return fieldMessage;
    }
  }

  if (typeof body.title === 'string' && body.title.length > 0) {
    return body.title;
  }

  return null;
}

/**
 * Maps an HTTP error to a human-readable message for toasts and inline UI.
 */
export function resolveHttpErrorMessage(error: HttpErrorResponse): string {
  const body = readErrorBody(error);
  const serverMessage =
    typeof body?.message === 'string' ? body.message : undefined;
  const validationMessage = resolveHttpValidationMessage(error);

  switch (error.status) {
    case 0:
      return 'Network error — please check your connection.';
    case 400:
      return validationMessage ?? serverMessage ?? 'Invalid request.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
    case 502:
    case 503:
      return 'A server error occurred. Please try again later.';
    default:
      return (
        validationMessage ??
        serverMessage ??
        `Unexpected error (${error.status}).`
      );
  }
}
